import { useTokens } from '../shared/theme.jsx';
import InstitutionMark from '../shared/InstitutionMark.jsx';
import ProfileAvatar from '../shared/ProfileAvatar.jsx';
import AccountMenu from '../shared/AccountMenu.jsx';
import MobilePillNav from '../shared/MobilePillNav.jsx';
import MobileAccountFooter from '../shared/MobileAccountFooter.jsx';
import PageBreadcrumb from '../shared/PageBreadcrumb.jsx';
import {
  CollapsibleRailHeader,
  RailNavIcon,
  useCollapsibleRail,
} from '../shared/CollapsibleRail.jsx';

const NAV_GROUPS = [
  {
    label: 'Program',
    items: [
      { id: 'overview', label: 'Overview', icon: 'overview' },
      { id: 'analytics', label: 'Analytics', icon: 'analytics' },
      { id: 'ai', label: 'Coffee Chats AI', icon: 'ai' },
      { id: 'seats', label: 'Seats & funding', icon: 'seats' },
    ],
  },
  {
    label: 'Account',
    items: [
      { id: 'admins', label: 'Admins', icon: 'users' },
      { id: 'billing', label: 'Billing & invoices', icon: 'financials' },
      { id: 'branding', label: 'Branding', icon: 'branding' },
      { id: 'audit', label: 'Audit log', icon: 'audit' },
      { id: 'support', label: 'Support', icon: 'support' },
    ],
  },
];

const PAGE_META = {
  // Overview leads with the school identity header instead of a description.
  overview: ['Overview', ''],
  analytics: ['Analytics', 'Privacy-safe adoption, engagement, and outcome reporting.'],
  ai: ['Coffee Chats AI', 'Ask across your program data, billing, invoices, policies, and portal guidance.'],
  seats: ['Seats & funding', 'Manage sponsored access and school-funded usage.'],
  billing: ['Billing & invoices', 'Review billing status and institution invoice history.'],
  admins: ['Admins', 'Invite administrators and control portal access.'],
  branding: ['Branding', 'Manage how your school appears across the institution portal.'],
  audit: ['Audit log', 'Review administrative changes and see who made them.'],
  support: ['Support', 'Send feedback or request help from the Coffee Chats team.'],
};

function AdminIdentity({
  profile, role, onSignOut, onSupport, onTwoFactor, signingOut, showSignOut, collapsed = false, compact = false, t,
}) {
  const fullName = profile?.name?.trim() || profile?.email?.split('@')[0] || 'Administrator';
  const firstName = fullName.split(/\s+/)[0];
  const initials = fullName
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => { return part.charAt(0).toUpperCase(); })
    .join('') || 'A';
  const normalizedRole = role?.toUpperCase();
  const roleLabel = normalizedRole === 'SUPER_ADMIN'
    ? 'Super admin'
    : normalizedRole === 'OWNER' ? 'Owner' : 'Admin';

  return (
    <div
      className={compact ? 'pt-2' : 'pt-4 border-t'}
      style={compact ? undefined : { borderColor: t.hairline }}
    >
      <AccountMenu
        profile={profile}
        onSignOut={onSignOut}
        onSupport={onSupport}
        onTwoFactor={onTwoFactor}
        busy={signingOut}
        showSignOut={showSignOut}
        t={t}
        interactionBackgroundColor={t.statBg}
        triggerClassName={collapsed ? 'p-0 flex justify-center' : compact ? 'p-2' : 'p-2 -m-2'}
        menuClassName={compact
          ? 'right-0 top-full mt-2 w-72'
          : collapsed ? 'left-full bottom-0 ml-3 w-72' : '-left-5 bottom-full mb-3 w-56'}
        renderTrigger={() => {
          return (
            <div className={`flex items-center min-w-0 ${collapsed ? 'justify-center' : 'gap-3'}`}>
              <ProfileAvatar
                profile={profile}
                initials={initials}
                className="h-10 w-10 text-sm"
                style={{ backgroundColor: t.statBg, color: t.heading, border: `1px solid ${t.border}` }}
              />
              {!collapsed ? <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-semibold" style={{ color: t.heading }}>{firstName}</div>
                <span
                  className="inline-flex mt-1 rounded px-2 py-0.5 text-[13px] leading-5 font-semibold uppercase"
                  style={{ color: t.primaryText, backgroundColor: t.primaryBg, fontFamily: t.fonts.body }}
                >
                  {roleLabel}
                </span>
              </div> : null}
            </div>
          );
        }}
      />
    </div>
  );
}

const ACCESS_RULES = {
  analytics: { scopes: ['ANALYTICS_VIEW'] },
  seats: { scopes: ['SEATS_MANAGE', 'BILLING_VIEW'] },
  admins: { scopes: ['ADMINS_MANAGE'], allowOwner: true },
  billing: { scopes: ['BILLING_VIEW'] },
  branding: { scopes: ['BRANDING_MANAGE', 'ADMINS_MANAGE'], allowOwner: true },
  audit: { scopes: ['ADMINS_MANAGE'] },
};

const withAccessState = (groups, context) => {
  if (!context) { return groups; }
  const scopes = context.institutionAdmin?.scopes || [];
  const owner = context.institutionAdmin?.role === 'OWNER';
  return groups.map((group) => {
    return {
      ...group,
      items: group.items.map((item) => {
        const rule = ACCESS_RULES[item.id];
        if (!rule || context.superAdmin) { return { ...item, locked: false }; }
        const allowed = (rule.allowOwner && owner)
          || rule.scopes.some((scope) => { return scopes.includes(scope); });
        return { ...item, locked: !allowed };
      }),
    };
  });
};

function NavItems({ groups, activeView, onNavigate, collapsed = false, t }) {
  return (
    <div className={`flex flex-col ${collapsed ? 'gap-4' : 'gap-6'}`}>
      {groups.map((group) => {
        return (
          <div key={group.label}>
            {!collapsed ? (
              <div
                className="px-3 mb-2 text-[13px] uppercase tracking-[0.12em]"
                style={{ color: t.muted, fontFamily: t.fonts.mono }}
              >
                {group.label}
              </div>
            ) : null}
            <div className="flex flex-col gap-1">
              {group.items.map((item) => {
                const active = item.id === activeView;
                let color = item.locked ? t.muted : t.body;
                if (active) { color = t.heading; }
                return (
                  <button
                    key={item.id}
                    type="button"
                    aria-current={active ? 'page' : undefined}
                    aria-label={collapsed ? item.label : undefined}
                    aria-description={item.locked ? 'Permission required' : undefined}
                    title={collapsed ? item.label : undefined}
                    onClick={() => { onNavigate(item.id); }}
                    className={`relative min-h-[44px] rounded-md text-[15px] whitespace-nowrap transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 ${t.focusRing} ${collapsed ? 'w-full px-0 flex items-center justify-center' : 'px-3 text-left flex items-center gap-3'}`}
                    style={{
                      color,
                      backgroundColor: active ? t.statBg : 'transparent',
                      fontWeight: active ? 600 : 400,
                      boxShadow: active ? `inset 3px 0 0 ${t.accent}` : 'none',
                    }}
                  >
                    <RailNavIcon name={item.icon} />
                    {!collapsed ? (
                      <>
                        <span className="min-w-0 truncate">{item.label}</span>
                        {item.locked ? (
                          // A lock glyph, not a word: "Billing & invoices" plus a
                          // text tag does not fit the 240px rail.
                          <span className="ml-auto shrink-0 flex items-center" style={{ color: t.muted }}>
                            <RailNavIcon name="lock" size={14} />
                            <span className="sr-only">Locked</span>
                          </span>
                        ) : null}
                      </>
                    ) : null}
                  </button>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default function PortalShell({
  name,
  slug,
  displayName,
  accent,
  logoFileId,
  logoSource,
  autoLogoDomain,
  activeView,
  onNavigate,
  profile,
  role,
  accessContext,
  onSignOut,
  onTwoFactor,
  signingOut,
  showSignOut = false,
  chromeHidden = false,
  // Optional plain-text program status beside the Overview welcome line
  // (for example "Setup in progress"). Nothing renders when it is empty.
  programStatus = null,
  children,
}) {
  const t = useTokens();
  const schoolName = displayName || name || 'Your school';
  const [pageTitle, pageDescription] = PAGE_META[activeView] || PAGE_META.overview;
  const { collapsed, toggle } = useCollapsibleRail();
  const navigationGroups = withAccessState(NAV_GROUPS, accessContext);
  const immersiveAi = activeView === 'ai';
  const overviewHeader = activeView === 'overview';
  const fullName = profile?.name?.trim() || profile?.email?.split('@')[0] || '';
  const firstName = fullName.split(/\s+/)[0];
  const accentRule = accent || t.border;
  const markProps = {
    name: schoolName, slug, logoFileId, logoSource, autoLogoDomain,
  };
  // Phones: the capsule carries the school's crest, name and accent rule.
  const mobileIdentity = (
    <div className="flex items-center gap-2.5 min-w-0">
      <InstitutionMark {...markProps} size={28} />
      <div className="min-w-0">
        <div
          className="truncate text-[15px] leading-tight font-semibold"
          style={{ color: t.heading, fontFamily: t.fonts.body }}
        >
          {schoolName}
        </div>
        <div className="mt-1 h-[3px] w-10" style={{ backgroundColor: accentRule }} />
      </div>
    </div>
  );

  return (
    <div
      className="min-h-screen w-full flex"
      style={{ backgroundColor: t.page, color: t.body, fontFamily: t.fonts.body }}
    >
      <aside
        data-collapsed={collapsed ? 'true' : 'false'}
        className={`hidden min-[900px]:flex shrink-0 h-screen sticky top-0 border-r py-6 flex-col overflow-x-hidden ${collapsed ? 'w-16' : 'w-60'}`}
        style={{
          backgroundColor: t.surface,
          borderColor: t.hairline,
          transition: 'width 200ms ease-out',
        }}
        aria-hidden={chromeHidden || undefined}
        inert={chromeHidden || undefined}
      >
        <CollapsibleRailHeader collapsed={collapsed} onToggle={toggle} t={t} />
        <nav
          aria-label="Institution portal"
          className={`mt-6 ${collapsed ? 'px-2' : 'px-5'}`}
        >
          <NavItems
            groups={navigationGroups}
            activeView={activeView}
            onNavigate={onNavigate}
            collapsed={collapsed}
            t={t}
          />
        </nav>
        <div className={`mt-auto ${collapsed ? 'px-2' : 'px-5'}`}>
          {profile || showSignOut ? (
            <AdminIdentity
              profile={profile}
              role={role}
              onSignOut={onSignOut}
              onSupport={() => { onNavigate('support'); }}
              onTwoFactor={onTwoFactor}
              signingOut={signingOut}
              showSignOut={showSignOut}
              collapsed={collapsed}
              t={t}
            />
          ) : (
            <div className="border-t pt-4 text-xs" style={{ borderColor: t.hairline, color: t.muted }}>
              Secure institution portal
            </div>
          )}
        </div>
      </aside>

      <div className="flex-1 min-w-0">
        <MobilePillNav
          ariaLabel="Institution portal"
          groups={navigationGroups}
          activeId={activeView}
          onSelect={onNavigate}
          className="min-[900px]:hidden"
          hidden={chromeHidden}
          identity={mobileIdentity}
          t={t}
          account={profile || showSignOut ? (
            <MobileAccountFooter
              profile={profile}
              role={role}
              onSignOut={onSignOut}
              onTwoFactor={onTwoFactor}
              signingOut={signingOut}
              showSignOut={showSignOut}
              t={t}
            />
          ) : null}
        />
        <div
          className="min-[900px]:hidden"
          style={{ height: 'calc(88px + env(safe-area-inset-top, 0px))' }}
          aria-hidden="true"
        />

        <div className={immersiveAi ? 'w-full' : 'mx-auto w-full max-w-[1240px] px-4 sm:px-6 lg:px-8'}>
          {!immersiveAi ? (
            <header
              style={{ borderColor: t.hairline }}
              aria-hidden={chromeHidden || undefined}
              inert={chromeHidden || undefined}
            >
              {overviewHeader ? (
                // Overview: crest + school name + accent rule + welcome line
                // (DESIGN.md portal header). Phones keep the name as an sr-only
                // h1 and lead with the welcome line; the capsule already shows
                // the crest, name and rule.
                <div className="py-5 min-[900px]:py-6 border-b">
                  <div className="flex items-center gap-4 min-w-0">
                    <div className="hidden min-[900px]:block shrink-0">
                      <InstitutionMark {...markProps} size={48} />
                    </div>
                    <div className="min-w-0">
                      <h1
                        className="sr-only min-[900px]:not-sr-only text-[30px] leading-tight"
                        style={{ color: t.heading, fontFamily: t.fonts.display, fontWeight: 500 }}
                      >
                        {schoolName}
                      </h1>
                      <div className="hidden min-[900px]:block mt-2 h-[3px] w-16" style={{ backgroundColor: accentRule }} />
                    </div>
                  </div>
                  {!chromeHidden ? (
                    <div className="min-[900px]:mt-4 flex flex-wrap items-baseline gap-x-3 gap-y-1">
                      <h2
                        className="text-[20px] leading-tight"
                        style={{ color: t.heading, fontFamily: t.fonts.display, fontWeight: 500 }}
                      >
                        Welcome back{firstName ? `, ${firstName}` : ''}
                      </h2>
                      {programStatus ? (
                        <p className="text-base" style={{ color: t.muted }}>{programStatus}</p>
                      ) : null}
                    </div>
                  ) : null}
                </div>
              ) : (
                <>
                  {/* Mobile: breadcrumb only (matches main-app dashboard trail);
                      its current-page label is the phone h1. */}
                  <div className="min-[900px]:hidden border-b py-2">
                    <PageBreadcrumb
                      rootLabel="Portal"
                      currentLabel={pageTitle}
                      onRootClick={() => { onNavigate('overview'); }}
                      t={t}
                    />
                  </div>

                  {/* Desktop / tablet: full page title + school mark. */}
                  <div className="hidden min-[900px]:flex min-h-[112px] py-6 border-b items-center justify-between gap-6">
                    <div className="min-w-0">
                      <h1
                        className="text-[28px] leading-tight"
                        style={{ color: t.heading, fontFamily: t.fonts.display, fontWeight: 500 }}
                      >
                        {pageTitle}
                      </h1>
                      {pageDescription ? (
                        <p className="mt-1 text-base" style={{ color: t.muted }}>
                          {pageDescription}
                        </p>
                      ) : null}
                    </div>
                    <div className="shrink-0">
                      <InstitutionMark {...markProps} size={48} showName />
                      <div className="mt-2 ml-auto h-[3px] w-16" style={{ backgroundColor: accentRule }} />
                    </div>
                  </div>
                </>
              )}
            </header>
          ) : null}

          {/* The header already ends in a hairline, so the first section drops its
              own top rule instead of drawing a second one 24px below. */}
          <main className={immersiveAi ? 'min-h-[calc(100vh-88px)] min-[900px]:min-h-screen' : 'py-4 min-[900px]:py-6 min-h-[calc(100vh-180px)] [&>section:first-child]:border-t-0 [&>section:first-child]:pt-0 [&>div:first-child>section:first-child]:border-t-0 [&>div:first-child>section:first-child]:pt-0'}>
            {children}
          </main>

          {!immersiveAi ? <footer
            className="py-5 border-t text-[12px] flex flex-wrap items-center justify-between gap-2"
            style={{ borderColor: t.hairline, color: t.muted, fontFamily: t.fonts.mono }}
            aria-hidden={chromeHidden || undefined}
            inert={chromeHidden || undefined}
          >
            <span>{(slug || 'your-school')}.coffeechats.me</span>
            <span>Powered by Coffee Chats</span>
          </footer> : null}
        </div>
      </div>
    </div>
  );
}
